import React, { useRef, useState } from "react";

import classNames from "classnames";
import PropTypes from "prop-types";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";

import useMediaQuery from "../../hooks/haiku/useMediaQuery";
import { ReactComponent as IconTranscript18 } from "../../icons/icon/18/a-icon-transcript-18.svg";
import KontentItemImage from "../../templates/kontentItemImage";
import SimpleRichText from "../../templates/text/simpleRichText";
import Button from "../button/button";
import ContentOverlay from "../contentOverlay/contentOverlay";
import MuteButton from "../video/muteButton";
import PlayButton from "../video/playButton";
import PlayVideoButton from "../video/playVideoButton";
import VideoBackgroundPlayer from "../video/videoBackgroundPlayer";

import "./heroHeadline.scss";

const HeroHeadline = ({
  children,
  data = null,
  eyecatcher = null,
  buttons = null,
  tabNavigation = null,
}) => {
  const isXs = useMediaQuery("(max-width: 575px)", true);

  const videoPlayer = useRef(null);
  const transcriptRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(true);

  let hideMuteButton = false;
  const hasButtons = buttons && (
    (Array.isArray(buttons) && buttons.length > 0) || !Array.isArray(buttons)
  );

  const isVideo = data?.value?.length > 0 && data.value[0].system.type === "video";
  let convertedData = null;
  let vttUrl;
  let transcript;
  let placeholderImage;

  if (isVideo) {
    const video = data?.value ? data.value[0] : data;
    convertedData = JSON.parse(video.elements.convert_video.value);
    hideMuteButton = video.elements.hide_mute_button.value.length > 0 && video.elements.hide_mute_button.value[0].codename === "true";
    placeholderImage = video.elements.placeholder_image.value;
    vttUrl = video.elements.webvtt.value.length > 0
      ? video.elements.webvtt.value[0].url
      : undefined;

    if (video.elements.transcript.value.length > 0) {
      const transcriptData = video.elements.transcript.value[0].elements;

      transcript = (
        <ContentOverlay
          ref={transcriptRef}
          button={(
            <Button variant="text-light">
              <IconTranscript18 className="mr-3" /> {transcriptData.button_text.value}
            </Button>
          )}
          headline={transcriptData.title.value}
        >
          <SimpleRichText data={transcriptData.text} />
        </ContentOverlay>
      );
    }
  }

  return (
    <div className="container--fullwidth">
      <div
        className={classNames(
          "hero-headline",
          {
            "hero-headline--eyecatcher": eyecatcher !== null,
            "hero-headline--tab-navigation": tabNavigation !== null,
          },
        )}
      >
        {isVideo
          ? (
            <div className="hero-headline__gradient">
              <div className="container-fluid">
                <VideoBackgroundPlayer
                  vttUrl={vttUrl}
                  src={convertedData}
                  ref={videoPlayer}
                  playing={playing}
                  setPlaying={setPlaying}
                  muted={muted}
                  setMuted={setMuted}
                  placeholderImage={placeholderImage}
                  transcriptRef={transcriptRef}
                >
                  <div className="video-background-player__control-container">
                    <PlayButton playing={playing} setPlaying={setPlaying} />
                    {!hideMuteButton && <MuteButton muted={muted} setMuted={setMuted} />}
                  </div>
                  <h1 className="hero-headline__headline">{children}</h1>
                  {hasButtons && eyecatcher === null
                    && (
                      <div className="hero-headline--button-container">
                        {buttons}
                        {!isXs && <PlayVideoButton videoPlayerRef={videoPlayer} />}
                      </div>
                    )}
                  {(isXs || !hasButtons) && <PlayVideoButton videoPlayerRef={videoPlayer} />}
                  {tabNavigation}
                </VideoBackgroundPlayer>
                <div className="container--fullwidth video-background-player__transcript-button">
                  {transcript}
                </div>
              </div>
            </div>
          )
          : (
            <>
              <div className="hero-headline__gradient">
                <div className="container-fluid">
                  <Row>
                    <Col lg={10}>
                      <h1 className="hero-headline__headline">{children}</h1>
                      {hasButtons && eyecatcher === null
                        && (
                          <div className="hero-headline--button-container">
                            {buttons}
                          </div>
                        )}
                    </Col>
                  </Row>
                  {tabNavigation}
                </div>
              </div>
              <KontentItemImage
                data={data}
                width={1920}
                height={660}
                imageOnlyWidth
                className="img-fluid"
                loading="eager"
              />
            </>
          )}
      </div>
      {eyecatcher !== null && (
        <Container fluid className="hero-headline__eyecatcher-container">
          {eyecatcher}
        </Container>
      )}
    </div>
  );
};

HeroHeadline.propTypes = {
  buttons: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  children: PropTypes.node.isRequired,
  data: PropTypes.oneOfType([PropTypes.object]),
  eyecatcher: PropTypes.oneOfType([PropTypes.object]),
  tabNavigation: PropTypes.oneOfType([PropTypes.object]),
};

export default HeroHeadline;
